import { memo, useCallback, useEffect, useState } from 'react';

import PlainModal, {
  PlainModalHeader,
  PlainModalContent,
  PlainModalFooter,
  type HandleModalDisplayed,
} from 'app/common/ui-next/plain-modal';

import OrgsDataProvider from 'app/dataProviders/org/OrgsDataProvider';
import { POST_COPY_ITEMS_BETWEEN_SCHOOLS } from 'app/common/api/requests/food/items/copy_items_between_schools';
import { api } from 'app/api';
import usePopup from 'app/common/hooks/usePopup';
import getNumEnding from 'app/common/lib/getNumEnding';
import * as orgsTypes from 'app/common/constants/schools/types';

import CopyCheckedControls from './CopyCheckedControls';
import styles from './CopyCheckedModal.module.scss'
import { CopyCheckedSelector } from './CopyCheckedSelector';

type OrgFilterItem = {
  cafeteria_enabled?: boolean | null,
  type?: string | null,
}

const orgsFilterCb = (item: OrgFilterItem): boolean => {
  const { cafeteria_enabled: cafeteriaEnabled, type } = item;
  if (type === orgsTypes.ENTERPRISE) return Boolean(cafeteriaEnabled);
  return true;
};

type Props = {
  orgId: number,
  show: boolean,
  names: Array<string>,
  ids: Array<number>,
  onDisplayStatusChange: (value: boolean) => void,
}



export const CopyCheckedModal = memo(({
  orgId,
  show,
  names,
  ids,
  onDisplayStatusChange,
}: Props) => {
  const popup = usePopup();
  const [saving, setSaving] = useState(false);
  const [selectedOrgs, setSelectedOrgs] = useState<Array<string>>([]);

  const nothingSelected = selectedOrgs.length === 0;

  useEffect(() => {
    if (!show) {
      setSelectedOrgs([]);
    }
  }, [show]);

  const submit = useCallback(async (_form: HTMLFormElement) => {
    setSaving(true);

    try {
      const orgsTo = selectedOrgs
        .map(id => Number.parseInt(id, 10))
        .filter(id => Number.isInteger(id) && id > 0);
      const res = await Promise.all(orgsTo.map(id => api.request(POST_COPY_ITEMS_BETWEEN_SCHOOLS, {
        error: `Не удалось скопировать продукцию в\u00A0заведение с\u00A0идентификатором ${id}`,
        params: {
          school_id_from: orgId,
          school_id_to: id,
          items_ids: ids,
        },
      })));

      if (res.every(result => result === 'success')) {
        const ending = getNumEnding(orgsTo.length, ['заведение', 'заведения', 'заведений']);
        popup(`Продукция успешно скопирована в\u00A0${orgsTo.length}\u00A0${ending}`);
        return true;
      }

      return false;
    } finally {
      setSaving(false);
    }
  }, [selectedOrgs, ids, orgId, popup]);

  return (
    <OrgsDataProvider
      orgId={orgId}
      filterCb={orgsFilterCb}
      excludeCurrent
      sameType
    >
      <PlainModal
        formId="copyTo"
        show={show}
        onSubmit={submit}
        onDisplayStatusChange={onDisplayStatusChange}
        className={styles.container}
      >
        <PlainModalHeader>
          {'Копировать в другое заведение'}
        </PlainModalHeader>
        <PlainModalContent maxHeight="70vh">
          <CopyCheckedSelector
            names={names}
            disabled={saving}
            show={show}
            onChangeSelection={setSelectedOrgs}
          />
        </PlainModalContent>
        <PlainModalFooter>
          {(toggleModal: HandleModalDisplayed) => (
            <CopyCheckedControls
              toggleModal={toggleModal}
              saving={saving}
              disableSubmit={nothingSelected}
            />
          )}
        </PlainModalFooter>
      </PlainModal>
    </OrgsDataProvider>
  );
});
